import { ShiftType } from "@/graphql/__generated__/graphql";
import { Decimal } from "decimal.js";
import { convertBpToDecimal, formatNumberLocale, sortByShiftType, convertServiceToPriceObject } from "@/utils/helpers";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionContent, AccordionTrigger } from "@/components/ui/accordion";
import { AccordionItem } from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { TFunction } from "i18next";
import { IntlShape, useIntl } from "react-intl";
import { TextInfoGroupAlt } from "@/components/shared";
import { Separator } from "@/components/ui/separator";
import { JSX } from "react";
import { Service } from "@/graphql/__generated__/graphql";
import { cn } from "@/lib/utils";

/**
 * Renders a detailed card displaying price information.
 *
 * @param {IntlShape} intl - The internationalization object to format numbers.
 * @param {PriceObject} priceObject - The price object containing details to display.
 * @returns {JSX.Element} The rendered detail card.
 *
 * @example
 * ```javascript
 * const card = renderDetailCard(intl, priceObject);
 * return <div>{card}</div>;
 * ```
 */
const renderDetailCard = (t: TFunction, intl: IntlShape, priceObject: PriceObject): JSX.Element => {
  const serviceDetailsMetrics = [
    {
      label: t("common:pricing.labels.margin"),
      value: `${priceObject.margin}%`,
    },
    {
      label: t("common:pricing.labels.totalemployeecount"),
      value: formatNumberLocale(intl, priceObject.totalEmployeeCount ?? 0, "decimal"),
    },
    {
      label: t("common:pricing.labels.hours"),
      value: new Decimal(priceObject.minutes?.cumulated ?? 0).dividedBy(60).toNumber().toFixed(2),
    },
    {
      label: t("common:pricing.labels.hoursbriefing"),
      value: `${priceObject.hoursBriefing ?? 0}h`,
    },
    {
      label: t("common:pricing.labels.transferredemployeepercentage"),
      value: priceObject.transferredEmployeePercentage == null ? null : `${convertBpToDecimal(priceObject.transferredEmployeePercentage)}%`,
    },
    {
      label: t("common:pricing.labels.pauseincluded"),
      value: priceObject.pauseIncluded == null ? null : priceObject.pauseIncluded ? t("common:yes") : t("common:no"),
    },
  ];

  const cleanedServiceDetailsMetrics = serviceDetailsMetrics.filter((metric) => metric.value !== null);

  // Base Details JSX
  const serviceDetail = (
    <div className="flex flex-col gap-2 grow">
      <div className="flex flex-col gap-1">
        <div className="text-sm font-bold">
          {t("translation:edittender.calculationoverview.labels.basepricedetails.label")}
        </div>
        <div className="text-xs text-muted-foreground">
          {t("translation:edittender.calculationoverview.labels.basepricedetails.description")}
        </div>
      </div>
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
        {cleanedServiceDetailsMetrics.map((metric) => {
          return (
            <TextInfoGroupAlt
              key={`metric-${metric.label}`}
              label={metric.label}
              content={metric.value}
              hasBackground={true}
              backgroundColor="slate"
            />
          );
        })}
      </div>
    </div>
  );

  return (
    <Card className="shadow-none">
      <AccordionItem value={priceObject.id.toString()} className="w-full">
        <AccordionTrigger className="text-start font-normal flex flex-col md:flex-row gap-4 hover:no-underline hover:bg-slate-50 px-4 pt-2">
          {serviceDetail}
        </AccordionTrigger>
        <AccordionContent onWheelCapture={(e) => e.stopPropagation()} className="px-4 pb-2">
          <Separator className="mb-4" />
          {
            priceObject.pricing && sortByShiftType(priceObject.pricing.servicePricing).map((detail) => (
              <div key={`pricing-detail-${detail.type}`}>
                <div
                  className={cn(
                    "flex flex-wrap items-center rounded-md px-2 py-1",
                    detail.type === ShiftType.Cumulated ? "bg-gray-50 hover:bg-slate-100" : "hover:bg-slate-50"
                  )}
                >
                  <div className="font-semibold w-1/4 mb-1">{t(`common:costdetailstype.${detail.type.toLowerCase()}`)}</div>
                  <div className="flex flex-wrap w-3/4 gap-x-4 gap-y-2">
                    <TextInfoGroupAlt
                      className="w-[150px]"
                      label={t("common:pricing.labels.revenue")}
                      content={formatNumberLocale(intl, detail.revenue, "currency")}
                    />
                    <TextInfoGroupAlt
                      className="w-[100px]"
                      label={t("common:pricing.labels.priceperhour")}
                      content={formatNumberLocale(intl, detail.pricePerHour, "currency")}
                    />
                    <TextInfoGroupAlt
                      className="w-[100px]"
                      label={t("common:pricing.labels.hours")}
                      content={formatNumberLocale(
                        intl,
                        (priceObject.minutes?.[detail.type.toLowerCase() as keyof MinutesObject] ?? 0) / 60,
                        "decimal",
                        false,
                        false,
                        0,
                        2
                      )}
                    />
                  </div>
                </div>
                <Separator className="my-2" />
              </div>
            ))
          }
        </AccordionContent>
      </AccordionItem>
    </Card>
  );
};

interface PricingSectionProps {
  service: Service;
  durationMonths: number;
}

function PricingSection({ service, durationMonths }: PricingSectionProps) {
  const { t } = useTranslation(["components", "common"]);
  const intl = useIntl();
  const priceObject = convertServiceToPriceObject(service, durationMonths);

  return (
    <div>
      <Accordion type="single" collapsible>
        {renderDetailCard(t, intl, priceObject)}
      </Accordion>
    </div>
  );
}

export default PricingSection;
